<template>
    <v-card
      class="asset-grid-item__card"
      elevation="1"
      :style="selected?'border: 3px solid #f7f5f3; background: #f7f5f3':''"
      @click="onCardClick"
    >
        <v-card-subtitle>
          <span v-if="!title">{{item_id}}</span><span v-else>{{title}}</span>
          <span class="asset-grid-item__checkbox" @click.stop>
            <checkbox
              style="float:right"
              :outlined-when-unchecked="true"
              :model-value="checked"
              @update:model-value="setChecked"
            ></checkbox>
          </span>
        </v-card-subtitle>
        <v-card-text>
          <div class="asset-grid-item__chips mb-2">
            <v-chip
              v-if="showInValidationSet"
              x-small
              label
              outlined
              color="primary"
              class="mr-1 mb-1"
            >{{ $t('asset_grid.in_validation_set') }}</v-chip>
            <v-chip
              x-small
              label
              outlined
              :color="showAssetValidated ? 'success' : 'grey'"
              class="mr-1 mb-1"
            >{{ showAssetValidated ? $t('asset_grid.asset_validated') : $t('asset_grid.asset_not_validated') }}</v-chip>
          </div>
          <div class="asset-grid-item__media">
            <div v-if="annotationCountLabel" class="asset-grid-item__annotation-count text-caption">
              {{ annotationCountLabel }}
            </div>
            <annotated-image
              :max_height="200"
              @canvas_selected="selectItem"
              :image_url="image_url"
              :sensitive-blur="sensitiveBlur"
              :zoom_enabled="false"
              :hide_annotation_ui="true"
              :annotations="annotations"
            ></annotated-image>
          </div>
          <div
            v-if="metaDisplayRows.length"
            class="asset-grid-item__meta mt-2"
            data-testid="asset-grid-item-meta"
          >
            <div
              v-for="row in metaDisplayRows"
              :key="row.key"
              class="asset-grid-item__meta-row text-caption"
            >
              <strong>{{ row.key }}:</strong>
              <span class="asset-grid-item__meta-value">{{ formatMetaValue(row.value) }}</span>
            </div>
          </div>
          <span v-if="validation" class="label" :style="{backgroundColor: background_color}">{{validation}}</span>
        </v-card-text>
    </v-card>
</template>

<script>
import Checkbox from '@/components/Checkbox'
import AnnotatedImage from '@/components/AnnotatedImage'
import { assetGridMetaDisplayRows } from '@/utils/assetGridMeta'

export default {
  components: {Checkbox, AnnotatedImage},
  props: {
    checked: {required: true},
    item_id: {required: true},
    image_url: {required: true},
    /** Project policy: blur when meta does not match configured key/value. */
    sensitiveBlur: { type: Boolean, default: false },
    title: {default: false},
    annotations: {required: true},
    validation: {required: true},
    selected: {default: false},
    in_validation_set: { default: null },
    asset_validated: { default: null },
    /** Raw asset.meta (JSON string or object). */
    meta: { default: null },
    /** Configured meta keys from project settings. */
    metaFieldKeys: { type: Array, default: () => [] },
  },
  methods: {
    onCardClick(event) {
      if (event.target.closest('.asset-grid-item__checkbox')) {
        return;
      }
      // Thumbnail canvas clicks used to be ignored so fabric could emit
      // canvas_selected. If the image is not ready yet, that event never
      // fires and the annotate modal never opens (e2e + real clicks).
      this.$emit('select_item', this.item_id);
    },
    setChecked(checked) {
      this.$emit('select_toggled', this.item_id, checked)
    },
    selectItem() {
      this.$emit('select_item', this.item_id)
    },
    remove(asset_id, bboxId) {
      this.$emit('remove_item', asset_id, bboxId)
    },
    formatMetaValue(value) {
      const maxLength = 40;
      const text = value == null ? '' : String(value);
      if (text.length > maxLength) {
        return text.substring(0, maxLength) + '...';
      }
      return text;
    },
  },
  computed: {
    showInValidationSet() {
      const v = this.in_validation_set;
      return v === true || v === 'true';
    },
    showAssetValidated() {
      const v = this.asset_validated;
      return v === true || v === 'true';
    },
    annotationCount() {
      return Array.isArray(this.annotations) ? this.annotations.length : 0;
    },
    annotationCountLabel() {
      const n = this.annotationCount;
      if (n === 1) {
        return `${n} ${this.$t('set_label_groups.edit_annotation')}`;
      }
      if (n > 1) {
        return `${n} ${this.$t('set_label_groups.edit_annotations')}`;
      }
      return '';
    },
    metaDisplayRows() {
      return assetGridMetaDisplayRows(this.meta, this.metaFieldKeys);
    },
    background_color () {
        let red = "rgba(249,65,68)";
        let green = "rgba(144,190,109)"
        let orange = "rgba(248,150,30)"

        let validation = this.validation
        if(!validation) {
          return ''
        }
        
        if(validation.startsWith('Correct')) {
          return green;
        }
        else if(validation.startsWith('False negative')) {
          return orange;
        }
        else {
          return red;
        }
    }
  },
}
</script>

<style scoped>
.asset-grid-item__card {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.asset-grid-item__card >>> .v-card__text {
  overflow: hidden;
  min-width: 0;
  flex: 1 1 auto;
  min-height: 0;
}
.asset-grid-item__annotation-count {
  line-height: 1.25;
  margin-bottom: 4px;
}
.asset-grid-item__media {
  max-width: 100%;
  overflow: hidden;
  line-height: 0;
}
.asset-grid-item__media >>> canvas {
  max-width: 100% !important;
  vertical-align: top;
}
.asset-grid-item__meta {
  line-height: 1.35;
  word-break: break-word;
}
.asset-grid-item__meta-row + .asset-grid-item__meta-row {
  margin-top: 2px;
}
.asset-grid-item__meta-value {
  margin-left: 4px;
}
</style>
